import type { ComputedRef } from 'vue';
import { type RouteLocationRaw, useRouter } from 'vue-router';
import type { DsIconName } from '~/assets/ds/icons';
import { useOpportunityDetail } from '~/composables/opportunities/useOpportunityDetail';

// Onglets du dossier (Aperçu/Édition/Simulations/Analyse/Documents/Messages/Historique) : cibles,
// détection de l'onglet actif et navigation (route interne / écran ARC / popup) partagées par les
// onglets desktop (LayoutV2SubTabs) et la tab bar mobile (MobileApp). La config était recopiée des
// deux côtés — une divergence (route ou feature-string de window.open) a déjà causé un bug.
// `label` = libellé desktop, `shortLabel` = libellé mobile abrégé.
export interface OpportunityTab {
  key: string;
  label: string;
  shortLabel: string;
  // Typé sur le set DS : l'onglet traverse un `:name="tab.icon"`, hors de portée du compilateur.
  icon: DsIconName;
  match: string;
  kind: 'route' | 'arc' | 'popup';
  to?: RouteLocationRaw;
  url?: string;
}

// « Est-on dans un dossier ? » se lit sur la route seule. Exposé à part des onglets, qui ouvrent
// eux la query du dossier : le layout pose la question sur toutes les pages de l'app.
export function useInDossier (): ComputedRef<boolean> {
  const route = useRoute();
  return computed<boolean>(() =>
    typeof route.params.opportunityHashId === 'string' && route.params.opportunityHashId !== '');
}

export function useOpportunityTabs (): {
  tabs: ComputedRef<OpportunityTab[]>;
  inDossier: ComputedRef<boolean>;
  isActive: (tab: OpportunityTab) => boolean;
  tabHref: (tab: OpportunityTab) => string | undefined;
  openTab: (tab: OpportunityTab) => void;
} {
  const route = useRoute();
  // Importé de vue-router et non auto-importé : le mock global des tests porte sur ce module.
  const router = useRouter();
  const config = useRuntimeConfig();
  const { opportunity } = useOpportunityDetail();

  const arc = computed<string>(() => config.public.ARC_URL ?? '');
  const reqId = computed<string>(() => String(opportunity.value?.reqId ?? ''));
  const inDossier = useInDossier();
  const { isV2 } = useFeatureVersion();

  const tabs = computed<OpportunityTab[]>(() => [
    { key: 'preview', label: 'Aperçu', shortLabel: 'Aperçu', icon: 'dashboard', match: 'preview', kind: 'arc', url: `${arc.value}/gestion_dossier.php?id=${reqId.value}` },
    isV2('edition')
      ? { key: 'edit', label: 'Édition', shortLabel: 'Édition', icon: 'file-edit', match: 'edit', kind: 'route', to: { name: 'opportunities-opportunityHashId-edit', params: route.params } }
      : { key: 'edit', label: 'Édition', shortLabel: 'Édition', icon: 'file-edit', match: 'edit', kind: 'popup', url: `${arc.value}/ManageCustomer.php?id=${reqId.value}` },
    { key: 'simulation', label: 'Simulations', shortLabel: 'Simu', icon: 'calculator', match: 'simulation', kind: 'route', to: { name: 'opportunities-opportunityHashId-simulation-auto', params: route.params } },
    { key: 'analysis', label: 'Analyse', shortLabel: 'Analyse', icon: 'scale', match: 'analysis', kind: 'route', to: { name: 'opportunities-opportunityHashId-analysis-synthesis', params: route.params } },
    { key: 'documents', label: 'Documents', shortLabel: 'Docs', icon: 'folder-docs', match: 'documents', kind: 'route', to: { name: 'opportunities-opportunityHashId-documents-all', params: route.params } },
    { key: 'messages', label: 'Messages', shortLabel: 'Messages', icon: 'messages', match: 'messages', kind: 'route', to: { name: 'opportunities-opportunityHashId-messages-received', params: route.params } },
    { key: 'history', label: 'Activité', shortLabel: 'Activité', icon: 'history', match: 'history', kind: 'popup', url: `${arc.value}/status_history.php?id=${reqId.value}` },
  ]);

  const isActive = (tab: OpportunityTab): boolean =>
    String(route.name ?? '').includes(`opportunities-opportunityHashId-${tab.match}`);

  // `openTab` garde la main sur le clic simple ; le reste part au navigateur via le `href`.
  function tabHref (tab: OpportunityTab): string | undefined {
    if (tab.kind === 'route') { return tab.to && inDossier.value ? router.resolve(tab.to).href : undefined; }
    return tab.url || undefined;
  }

  function openTab (tab: OpportunityTab): void {
    if (tab.kind === 'route' && tab.to) {
      void navigateTo(tab.to);
    } else if (tab.kind === 'arc' && tab.url) {
      void navigateTo(tab.url, { external: true });
    } else if (tab.kind === 'popup' && tab.url && import.meta.client) {
      window.open(tab.url, '', 'scrollbars=no,resizable=yes,status=no,location=no,toolbar=no,menubar=no,directories=no');
    }
  }

  return { tabs, inDossier, isActive, tabHref, openTab };
}
